import { checklistService, Checklist, ChecklistItem } from '@/firebase/checklistService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { assetService, Asset } from '@/firebase/assetService';
import { assetFactsService, AssetFacts, AssetFact } from '@/firebase/assetFactsService';
import { checklistEvidenceService } from '@/firebase/checklistEvidenceService';
import { extractEvidenceFieldsForItem } from './autoCompleteChecklistService';
import { evaluateChecklistItemWithAI } from '@/services/geminiService';
import { validateItemCompletion } from '@/services/checklistValidationService';
import { serverTimestamp } from 'firebase/firestore';
import { Organization } from '@/firebase/organizationService';

export interface AutoCompleteFromFactsResult {
  success: boolean;
  checklistsProcessed: number;
  itemsCompleted: number;
  itemsPending: number;
  checklistsCompleted: number;
  checklistsPaused: number;
  errors?: string[];
}

/**
 * Auto-complete checklists for an asset using existing facts (no file reprocessing)
 * 
 * This function:
 * 1. Gets all checklists associated with the asset
 * 2. Loads existing facts for the asset
 * 3. Evaluates each checklist item based on facts
 * 4. Creates/updates checklist completion records
 * 5. Marks checklists as complete if all items satisfied, otherwise paused/pending
 */
export async function autoCompleteChecklistsFromFacts(
  assetId: string,
  organizationId: string,
  userId: string
): Promise<AutoCompleteFromFactsResult> {
  const result: AutoCompleteFromFactsResult = {
    success: true,
    checklistsProcessed: 0,
    itemsCompleted: 0,
    itemsPending: 0,
    checklistsCompleted: 0,
    checklistsPaused: 0,
    errors: [],
  };

  try {
    // 1. Get the asset
    const assetResult = await assetService.getAsset(assetId);
    if (!assetResult.success || !assetResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get asset: ${assetResult.error}`);
      return result;
    }

    const asset = assetResult.data;

    // 2. Get all checklists for this asset
    const checklistsResult = await checklistService.getAllChecklists(organizationId);
    if (!checklistsResult.success || !checklistsResult.data) {
      result.success = false;
      result.errors?.push(`Failed to get checklists: ${checklistsResult.error}`);
      return result;
    }

    // Filter checklists that match this asset
    const relevantChecklists = checklistsResult.data.filter(checklist => {
      if (checklist.assetTypeIds && checklist.assetTypeIds.length > 0) {
        if (!asset.assetTypeId || !checklist.assetTypeIds.includes(asset.assetTypeId)) {
          return false;
        }
      }

      if (checklist.locationIds && checklist.locationIds.length > 0) {
        if (!asset.locationId || !checklist.locationIds.includes(asset.locationId)) {
          return false;
        }
      }

      return true;
    });

    if (relevantChecklists.length === 0) {
      result.errors?.push('No relevant checklists found for this asset');
      return result;
    }

    // 3. Get existing facts for the asset
    const factsResult = await assetFactsService.getAssetFacts(assetId);
    if (!factsResult.success || !factsResult.data) {
      result.errors?.push('No facts found for asset. Please upload files first to extract facts.');
      return result;
    }

    const facts = factsResult.data.facts || {};

    // 4. Process each checklist
    for (const checklist of relevantChecklists) {
      try {
        const checklistResult = await processChecklistFromFacts(
          checklist,
          asset,
          facts,
          organizationId,
          userId
        );

        result.checklistsProcessed++;
        result.itemsCompleted += checklistResult.itemsCompleted;
        result.itemsPending += checklistResult.itemsPending;
        
        if (checklistResult.status === 'complete') {
          result.checklistsCompleted++;
        } else if (checklistResult.status === 'paused' || checklistResult.status === 'pending_review') {
          result.checklistsPaused++;
        }

        if (checklistResult.errors) {
          result.errors?.push(...checklistResult.errors);
        }
      } catch (error: any) {
        console.error(`Error processing checklist ${checklist.id}:`, error);
        result.errors?.push(`Failed to process checklist "${checklist.name}": ${error.message}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error auto-completing checklists from facts:', error);
    result.success = false;
    result.errors?.push(`Fatal error: ${error.message}`);
    return result;
  }
}

interface ProcessChecklistResult {
  itemsCompleted: number;
  itemsPending: number;
  status: 'complete' | 'paused' | 'pending_review' | 'in_progress';
  errors?: string[];
}

/**
 * Process a single checklist using existing facts
 */
async function processChecklistFromFacts(
  checklist: Checklist,
  asset: Asset,
  facts: Record<string, AssetFact>,
  organizationId: string,
  userId: string,
  organization?: Organization
): Promise<ProcessChecklistResult> {
  const result: ProcessChecklistResult = {
    itemsCompleted: 0,
    itemsPending: 0,
    status: 'in_progress',
    errors: [],
  };

  try {
    // Check if there's already a completion for this checklist and asset
    const existingCompletionsResult = await checklistCompletionService.getCompletionsForAsset(asset.id!);
    let existingCompletion: ChecklistCompletion | null = null;

    if (existingCompletionsResult.success && existingCompletionsResult.data) {
      existingCompletion = existingCompletionsResult.data.find(
        c => c.checklistId === checklist.id && c.assetId === asset.id
      ) || null;
    }

    // Create or update completion
    let completion: ChecklistCompletion;
    if (existingCompletion) {
      completion = existingCompletion;
    } else {
      completion = {
        checklistId: checklist.id!,
        checklistName: checklist.name,
        assetId: asset.id,
        assetName: asset.assetName,
        locationId: asset.locationId,
        variableValues: {},
        itemResults: {},
        completedBy: 'AUTO',
        completedByName: 'AUTO',
        startedAt: new Date(),
        status: 'in_progress',
        organizationId: organizationId,
      };
    }

    if (!completion.itemResults) {
      completion.itemResults = {};
    }

    const pendingItems: string[] = [];

    // Process each checklist item
    for (const item of checklist.items || []) {
      // First, try to extract field values from asset facts and create evidence records
      if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
        // Ensure completion is saved to get an ID
        let completionId = completion.id;
        if (!completionId) {
          const tempCompletion = {
            ...completion,
            status: 'paused' as const,
          };
          const createResult = await checklistCompletionService.addCompletion(tempCompletion);
          if (createResult.success && createResult.id) {
            completionId = createResult.id;
            completion.id = completionId;
          }
        }

        if (completionId) {
          // Process each evidence definition
          for (const evidenceDef of item.evidenceDefinitions) {
            if (!evidenceDef.fieldDefinitions || evidenceDef.fieldDefinitions.length === 0) {
              continue;
            }

            // Extract field values from asset facts - fact keys match field keys directly
            const fieldValues: Record<string, any> = {};
            const fieldData: Record<string, { value: any; populated: boolean; validated: boolean; source?: string }> = {};
            
            for (const fieldDef of evidenceDef.fieldDefinitions) {
              const fact = facts[fieldDef.key];
              let value: any = null;
              let source: string | undefined = undefined;
              
              if (fact) {
                // Get value and source from fact
                if (fact.source === 'REVIEWER_OVERRIDE' && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                  value = fact.value;
                  source = fact.extractedFrom || fact.overrideReason?.match(/from file:\s*([^\n]+)/i)?.[1];
                } else if (fact.extractedValues && fact.extractedValues.length > 0) {
                  // Use primary value (highest confidence)
                  const sorted = fact.extractedValues.sort((a: any, b: any) => {
                    const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                    return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                  });
                  value = sorted[0].value;
                  source = sorted[0].fileName;
                } else if (fact.value !== null && fact.value !== undefined && fact.value !== '') {
                  value = fact.value;
                  source = fact.extractedFrom;
                }
              } else {
                // Debug: check for key mismatches
                const factKeys = Object.keys(facts);
                const matchingKey = factKeys.find(k => 
                  k.toLowerCase() === fieldDef.key.toLowerCase() ||
                  k.replace(/\s+/g, '') === fieldDef.key.replace(/\s+/g, '') ||
                  k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === fieldDef.key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
                );
                if (matchingKey && matchingKey !== fieldDef.key) {
                  console.warn(`[AutoCompleteFromFacts] Field "${fieldDef.label}" key mismatch: looking for "${fieldDef.key}" but found "${matchingKey}" in asset facts`);
                  const matchedFact = facts[matchingKey];
                  if (matchedFact && matchedFact.value !== null && matchedFact.value !== undefined && matchedFact.value !== '') {
                    value = matchedFact.value;
                    source = matchedFact.extractedFrom;
                  }
                } else if (!fact && fieldDef.required) {
                  console.debug(
                    `[AutoCompleteFromFacts] Required field "${fieldDef.label}" (key: "${fieldDef.key}") not found in asset facts. ` +
                    `This is expected if documents haven't been reprocessed since this field was added to the checklist. ` +
                    `Please reprocess documents to extract this field.`
                  );
                }
              }
              
              if (value !== null && value !== undefined && value !== '') {
                fieldValues[fieldDef.key] = value;
              }
              
              // Store field data for evidence
              fieldData[fieldDef.key] = {
                value: value,
                populated: value !== null && value !== undefined && value !== '',
                validated: false, // Will be validated below
                source: source
              };
            }

            // Calculate field status
            const totalFields = evidenceDef.fieldDefinitions.length;
            const requiredFields = evidenceDef.fieldDefinitions.filter(f => f.required).length;
            const populatedFields = Object.values(fieldData).filter(f => f.populated).length;
            const missingFields: string[] = [];
            const invalidFields: string[] = [];
            
            // Validate each field using comprehensive validation
            const { validateFieldValueFromEvidenceField } = await import('@/services/checklistValidationService');
            const itemResult = completion.itemResults?.[item.text];
            
            for (const fieldDef of evidenceDef.fieldDefinitions) {
              const fieldInfo = fieldData[fieldDef.key];
              
              // Check if required field is missing
              if (fieldDef.required && !fieldInfo.populated) {
                missingFields.push(fieldDef.key);
                fieldInfo.validated = false;
                continue;
              }
              
              // If field is populated, validate it comprehensively
              if (fieldInfo.populated) {
                const value = fieldInfo.value;
                // Use comprehensive validation function
                const validationResult = validateFieldValueFromEvidenceField(
                  fieldDef,
                  value,
                  facts,
                  itemResult
                );
                
                if (validationResult.valid) {
                  fieldInfo.validated = true;
                } else {
                  invalidFields.push(fieldDef.key);
                  fieldInfo.validated = false;
                  // Store validation error in fieldData for display
                  (fieldInfo as any).validationError = validationResult.reason;
                }
              } else {
                // Optional field with no value - considered valid
                fieldInfo.validated = true;
              }
            }
            
            // Count validated fields (only required fields must be validated for evidence to be complete)
            const requiredFieldsValidated = evidenceDef.fieldDefinitions
              .filter(f => f.required)
              .every(f => {
                const fieldInfo = fieldData[f.key];
                return fieldInfo.populated && fieldInfo.validated;
              });
            
            const validatedFields = Object.values(fieldData).filter(f => f.validated).length;
            
            // Determine validation status
            // Evidence is COMPLETE only when:
            // 1. All required fields are populated
            // 2. All required fields pass validation
            // 3. All populated fields (required or optional) pass validation
            let validationStatus: 'COMPLETE' | 'INCOMPLETE' | 'INVALID';
            if (missingFields.length > 0) {
              validationStatus = 'INCOMPLETE';
            } else if (invalidFields.length > 0 || !requiredFieldsValidated) {
              validationStatus = 'INVALID';
            } else {
              validationStatus = 'COMPLETE';
            }
            
            // Generate validation summary
            let validationSummary = '';
            if (validationStatus === 'COMPLETE') {
              validationSummary = `All ${requiredFields} required fields populated and validated`;
            } else if (missingFields.length > 0) {
              const missingLabels = missingFields.map(key => {
                const fieldDef = evidenceDef.fieldDefinitions?.find(f => f.key === key);
                return fieldDef?.label || key;
              });
              validationSummary = `Missing required fields: ${missingLabels.join(', ')}`;
            } else if (invalidFields.length > 0) {
              const invalidDetails = invalidFields.map(key => {
                const fieldDef = evidenceDef.fieldDefinitions?.find(f => f.key === key);
                const fieldInfo = fieldData[key];
                const error = (fieldInfo as any).validationError || 'Invalid value';
                return `${fieldDef?.label || key}: ${error}`;
              });
              validationSummary = `Validation failed: ${invalidDetails.join('; ')}`;
            }

            // Create evidence record (even if no fields populated, to track evidence requirement)
            // Check if evidence already exists for this item and evidence name
            const existingEvidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
              completionId,
              item.text
            );

            // Get the target evidenceName (normalize for matching)
            const targetEvidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
            
            // Match evidence by evidenceName or categoryCode (for legacy records)
            // Use strict matching to avoid duplicates
            const allMatchingEvidence = existingEvidenceResult.success && existingEvidenceResult.data
              ? existingEvidenceResult.data.filter(ev => {
                  const evName = ev.evidenceName || (ev as any).categoryCode || '';
                  return evName === targetEvidenceName || 
                         evName === evidenceDef.evidenceName ||
                         evName === (evidenceDef as any).categoryCode;
                })
              : [];
            
            // If there are multiple matches, we need to deduplicate - keep the most recent one
            let existingEvidence = allMatchingEvidence.length > 0 ? allMatchingEvidence[0] : null;
            
            // If there are duplicates, delete the older ones and keep only the most recent
            if (allMatchingEvidence.length > 1) {
              console.log(`[AutoCompleteFromFacts] Found ${allMatchingEvidence.length} duplicate evidence records for "${targetEvidenceName}". Deduplicating...`);
              // Sort by addedAt (most recent first)
              const sorted = allMatchingEvidence.sort((a, b) => {
                const aTime = a.addedAt?.toMillis?.() || a.addedAt || 0;
                const bTime = b.addedAt?.toMillis?.() || b.addedAt || 0;
                return bTime - aTime;
              });
              existingEvidence = sorted[0]; // Keep the most recent
              
              // Delete the older duplicates
              for (let i = 1; i < sorted.length; i++) {
                if (sorted[i].id) {
                  console.log(`[AutoCompleteFromFacts] Deleting duplicate evidence record: ${sorted[i].id}`);
                  await checklistEvidenceService.deleteEvidence(sorted[i].id!);
                }
              }
            }

            if (!existingEvidence) {
              const evidenceResult = await checklistEvidenceService.addEvidence(
                {
                  checklistCompletionId: completionId,
                  itemText: item.text,
                  evidenceType: 'DATA_ONLY',
                  evidenceName: evidenceDef.evidenceName,
                  source: 'CORE_SYSTEM',
                  organizationId: organizationId,
                  addedByUserId: userId,
                  fieldStatus: {
                    totalFields,
                    requiredFields,
                    populatedFields,
                    validatedFields,
                    missingFields,
                    invalidFields,
                  },
                  validationStatus,
                  validationSummary,
                  fieldValues: fieldData,
                },
                organization || { id: organizationId } as any
              );

              if (!evidenceResult.success) {
                console.error(`Failed to create evidence for ${item.text}:`, evidenceResult.error);
              }
            } else {
              // Update existing evidence with new field values and validation status
              if (existingEvidence.id) {
                // Always ensure evidenceName is set (required field)
                const updateData: any = {
                  evidenceName: evidenceDef.evidenceName || existingEvidence.evidenceName || (existingEvidence as any).categoryCode || 'Unknown Evidence',
                  fieldStatus: {
                    totalFields,
                    requiredFields,
                    populatedFields,
                    validatedFields,
                    missingFields,
                    invalidFields,
                  },
                  validationStatus,
                  validationSummary,
                  fieldValues: fieldData,
                  updatedAt: serverTimestamp(),
                };
                
                await checklistEvidenceService.updateEvidence(existingEvidence.id, updateData);
              }
            }
          }
        }
      }

      // Step 1: Extract all evidence fields from asset facts (EXACT key matching)
      const extractedFields = extractEvidenceFieldsForItem(item, facts);
      
      // Step 2: Use AI to evaluate item status based on evidence fields
      // Only evaluate if item has evidence definitions (AI needs evidence context)
      if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
        try {
          const aiResult = await evaluateChecklistItemWithAI(item, extractedFields, facts);
          
          // Debug logging for AI result
          if (item.text.toLowerCase().includes('business purpose')) {
            console.log(`[AI Evaluation] AI result:`, aiResult);
          }
          
          // Validate required fields to ensure completeness
          const evidenceFields: Record<string, any> = {};
          Object.values(extractedFields).forEach(categoryFields => {
            Object.entries(categoryFields).forEach(([key, fieldValue]) => {
              if (fieldValue.value !== null && fieldValue.value !== undefined && fieldValue.value !== '') {
                evidenceFields[key] = fieldValue.value;
              }
            });
          });
          
          const validation = validateItemCompletion(
            item,
            completion.itemResults[item.text],
            facts,
            evidenceFields
          );
          
          // Determine final status: combine AI result with validation
          let finalStatus = aiResult.status;
          
          if (validation.status === 'INCOMPLETE' && aiResult.status === 'PASS') {
            // AI says PASS but validation says incomplete - trust validation
            finalStatus = 'NEEDS_REVIEW';
          } else if (validation.status === 'COMPLETE' && aiResult.status === 'NEEDS_REVIEW') {
            // Validation says complete but AI says needs review - trust AI
            finalStatus = 'NEEDS_REVIEW';
          }
          
          // Update or create item result
          // Always update if:
          // 1. No existing result, OR
          // 2. Existing result status is different from AI result, OR
          // 3. Existing result is NEEDS_REVIEW or FAIL (always allow updates from these)
          // Ensure itemResults is initialized
          if (!completion.itemResults) {
            completion.itemResults = {};
          }
          
          const existingResult = completion.itemResults[item.text];
          // Always update if status is different, or if no existing result
          const shouldUpdate = !existingResult || existingResult.status !== finalStatus;
          
          if (shouldUpdate) {
            // Track previous status for counter updates
            const previousStatus = existingResult?.status;
            
            // Ensure itemResults object exists
            if (!completion.itemResults) {
              completion.itemResults = {};
            }
            
          completion.itemResults[item.text] = {
              status: finalStatus,
              findingText: aiResult.findingText,
            updatedBy: userId,
            updatedAt: new Date(),
              // Store AI reasoning for transparency
              ...(aiResult.reasoning && { notes: `AI Reasoning: ${aiResult.reasoning}` }),
            };
            
            // Debug logging for status updates
            if (item.text.toLowerCase().includes('business purpose')) {
              console.log(`[AutoComplete] Updated item "${item.text}": ${previousStatus} -> ${finalStatus}`, {
                aiResult: aiResult.status,
                finalStatus,
                findingText: aiResult.findingText
              });
            }
            
            // Update counters based on status change
            if (finalStatus === 'PASS' || finalStatus === 'NA') {
              // Item is now complete
              if (!previousStatus || previousStatus === 'FAIL' || previousStatus === 'NEEDS_REVIEW' || previousStatus === 'NA') {
                // Was not complete before, now it is
            result.itemsCompleted++;
                if (previousStatus === 'FAIL' || previousStatus === 'NEEDS_REVIEW') {
                  result.itemsPending = Math.max(0, result.itemsPending - 1);
                  const index = pendingItems.indexOf(item.text);
                  if (index > -1) {
                    pendingItems.splice(index, 1);
                  }
                }
              }
            } else {
              // Item is now pending/needs review
              if (!previousStatus || previousStatus === 'PASS' || previousStatus === 'NA') {
                // Was complete before, now it's not
                if (previousStatus === 'PASS' || previousStatus === 'NA') {
                  result.itemsCompleted = Math.max(0, result.itemsCompleted - 1);
                }
            result.itemsPending++;
                if (!pendingItems.includes(item.text)) {
            pendingItems.push(item.text);
          }
              }
            }
          }
        } catch (aiError: any) {
          console.error(`[AI Evaluation] Error evaluating item "${item.text}":`, aiError);
          // Fall back to NEEDS_REVIEW if AI fails
          if (!completion.itemResults[item.text]) {
            completion.itemResults[item.text] = {
              status: 'NEEDS_REVIEW',
              findingText: 'AI evaluation failed - manual review required',
              updatedBy: userId,
              updatedAt: new Date(),
            };
            result.itemsPending++;
            pendingItems.push(item.text);
          }
        }
      } else {
        // No evidence definitions - mark as needs review
        if (!completion.itemResults[item.text]) {
          completion.itemResults[item.text] = {
            status: 'NEEDS_REVIEW',
            findingText: 'No evidence definitions - requires manual review',
            updatedBy: userId,
            updatedAt: new Date(),
          };
          result.itemsPending++;
          pendingItems.push(item.text);
        }
      }
    }

    // Determine completion status
    const totalItems = checklist.items?.length || 0;
    const completedItems = Object.values(completion.itemResults).filter(
      r => r.status === 'PASS' || r.status === 'NA'
    ).length;

    // Generate completion summary
    let completionSummary: {
      totalItems: number;
      completedItems: number;
      incompleteItems: number;
      missingRequiredFields: Array<{ itemText: string; missingFields: string[] }>;
      missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }>;
      needsReviewItems: string[];
    } | undefined = undefined;

    try {
      // No longer need to enrich - fields come from evidenceDefinitions
      const missingRequiredFields: Array<{ itemText: string; missingFields: string[] }> = [];
      const missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }> = [];
      const needsReviewItems: string[] = [];

      for (const item of checklist.items || []) {
        const itemResult = completion.itemResults[item.text];
        const validation = validateItemCompletion(item, itemResult, facts);

        if (validation.missingFields.length > 0) {
          missingRequiredFields.push({
            itemText: item.text,
            missingFields: validation.missingFields,
          });
        }

        if (item.evidenceDefinitions) {
          for (const evidenceDef of item.evidenceDefinitions) {
            if (evidenceDef.required && validation.status === 'INCOMPLETE') {
              missingRequiredEvidence.push({
                itemText: item.text,
                evidenceCategory: evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence',
              });
            }
          }
        }

        if (validation.status === 'NEEDS_REVIEW' || itemResult?.status === 'NEEDS_REVIEW') {
          needsReviewItems.push(item.text);
        }
      }

      completionSummary = {
        totalItems,
        completedItems,
        incompleteItems: totalItems - completedItems,
        missingRequiredFields,
        missingRequiredEvidence,
        needsReviewItems,
      };
    } catch (error) {
      console.error('Error generating completion summary:', error);
    }

    if (completedItems === totalItems && totalItems > 0) {
      // All items satisfied
      completion.status = 'complete';
      completion.completedAt = new Date();
      result.status = 'complete';
    } else {
      // Some items incomplete - set to paused for auto-completed checklists
      completion.status = 'paused';
      completion.pendingItems = pendingItems;
      completion.completionSummary = completionSummary;
      result.status = 'paused';
    }

    // Save or update completion
    if (completion.id) {
      // Ensure itemResults is initialized before saving
      if (!completion.itemResults) {
        completion.itemResults = {};
      }
      
      console.log(`[AutoComplete] Saving completion ${completion.id} with ${Object.keys(completion.itemResults).length} item results`);
      // Log business purpose item status for debugging
      const businessPurposeItem = Object.entries(completion.itemResults).find(([key]) => key.toLowerCase().includes('business purpose'));
      if (businessPurposeItem) {
        console.log(`[AutoComplete] Business purpose item status:`, businessPurposeItem[1]);
      }
      
      const updateResult = await checklistCompletionService.updateCompletion(
        completion.id,
        {
          itemResults: completion.itemResults,
          status: completion.status,
          pendingItems: completion.pendingItems,
          completedAt: completion.completedAt,
          completionSummary: completion.completionSummary,
          updatedAt: new Date(),
        }
      );
      if (!updateResult.success) {
        result.errors?.push(`Failed to update completion: ${updateResult.error}`);
        console.error(`[AutoComplete] Failed to update completion:`, updateResult.error);
      } else {
        console.log(`[AutoComplete] Successfully saved completion ${completion.id}`);
      }
    } else {
      const createResult = await checklistCompletionService.addCompletion({
        ...completion,
        completionSummary: completion.completionSummary,
      });
      if (!createResult.success) {
        result.errors?.push(`Failed to create completion: ${createResult.error}`);
      }
    }

    return result;
  } catch (error: any) {
    console.error('Error processing checklist from facts:', error);
    result.errors?.push(`Error: ${error.message}`);
    return result;
  }
}

